import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PenLine, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { PenMark } from "./BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHIP_STYLES = [
  "bg-banana text-ink",
  "bg-white text-ink",
  "bg-crimson text-cream",
  "bg-royal text-cream",
  "bg-gold text-ink",
];

export default function Pledge() {
  const [name, setName] = useState("");
  const [pledges, setPledges] = useState([]);
  const [count, setCount] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/pledge`)
      .then((r) => {
        setPledges(r.data.pledges || []);
        setCount(r.data.count || 0);
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await axios.post(`${API}/pledge`, { name });
      setPledges([r.data, ...pledges]);
      setCount(count + 1);
      setName("");
      toast.success(`${r.data.name} holds the pen!`, {
        description: "Welcome to the pledge wall — your story is yours to write.",
      });
    } catch {
      toast.error("Oops, try again", { description: "Add a first name (2–30 letters) to sign the wall." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="pledge" data-testid="pledge-section" className="halftone relative border-t-2 border-ink py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="text-center">
          <span className="sticker inline-flex -rotate-1 rounded-lg bg-white">
            <PenMark className="h-4 w-4" /> The reader pledge
          </span>
          <h2 className="mx-auto mt-5 max-w-2xl font-head text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            “I hold the <span className="text-maroon">pen</span>.”
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg">
            Young readers, sign the pledge wall: my story is mine to write — I add my own chapters and I change my own
            ending.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="pledge-form"
            onSubmit={submit}
            className="mx-auto mt-9 flex max-w-lg flex-col gap-3 sm:flex-row"
          >
            <input
              data-testid="pledge-input-name"
              required
              minLength={2}
              maxLength={30}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your first name"
              className="comic-input flex-1 text-center sm:text-left"
            />
            <button
              data-testid="pledge-submit-btn"
              type="submit"
              disabled={sending}
              className="flex items-center justify-center gap-2 rounded-full border-2 border-ink bg-crimson px-6 py-3 font-head text-sm font-extrabold uppercase tracking-wide text-cream shadow-pop transition-all duration-200 hover:-translate-y-0.5 hover:shadow-pop-lg active:translate-y-0 active:shadow-pop-sm disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
              Sign the pledge
            </button>
          </form>
        </Reveal>

        <div data-testid="pledge-wall" className="mt-12 flex flex-wrap items-center justify-center gap-3">
          {pledges.length === 0 && (
            <p className="font-mono text-sm uppercase tracking-widest text-ink/40">
              Be the first reader on the wall
            </p>
          )}
          {pledges.map((p, i) => (
            <motion.span
              key={p.id}
              initial={{ opacity: 0, scale: 0.6, rotate: 0 }}
              animate={{ opacity: 1, scale: 1, rotate: i % 2 === 0 ? -2 : 2 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className={`rounded-lg border-2 border-ink px-4 py-2 font-display text-base font-semibold shadow-pop-sm ${CHIP_STYLES[i % CHIP_STYLES.length]}`}
            >
              {p.name}
            </motion.span>
          ))}
          {count > pledges.length && (
            <span className="rounded-lg border-2 border-dashed border-ink/40 px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-ink/50">
              +{count - pledges.length} more readers
            </span>
          )}
        </div>

        <p data-testid="pledge-count" className="mt-8 text-center font-mono text-xs font-bold uppercase tracking-[0.3em] text-ink/50">
          {count} {count === 1 ? "reader holds" : "readers hold"} the pen
        </p>
      </div>
    </section>
  );
}
